import React, { useEffect, useState, useCallback } from 'react'
import { useParams, Link } from 'react-router-dom'
import { Breadcrumb } from '@/components/layout/Breadcrumb'
import { Button } from '@/components/ui/Button'
import { PageLoading, PageNotFound, PageError } from '@/components/ui/PageAsyncState'
import { useCan } from '@/features/auth/AuthContext'
import { Save } from 'lucide-react'
import {
  getSalesOrderApi,
  patchSalesOrderApi,
  updateSalesOrderLineStatusApi,
  searchVariantsApi,
  type SalesOrderDetail,
  type VariantSearchResult,
} from '@/services/salesOrders'
import { listCustomersApi, getCustomerApi, type Customer, type CustomerBuyer } from '@/services/customers'
import { formatDocumentError } from '@/utils/documentErrorMessages'
import {
  type GridLineItem,
  validateGridLine,
  buildPatchLinesPayload,
} from './salesOrderGridUtils'
import { SalesOrderGrid } from './SalesOrderGrid'
import { PasteExcelModal } from './PasteExcelModal'

export interface SalesOrderDetailPageProps {
  initialOrder?: SalesOrderDetail
}

export function SalesOrderDetailPage({ initialOrder }: SalesOrderDetailPageProps = {}): React.ReactElement {
  const { id } = useParams<{ id: string }>()
  const canWrite = useCan('sales.write')
  const canDemandWrite = useCan('demand.write')

  const [order, setOrder] = useState<SalesOrderDetail | null>(initialOrder ?? null)
  const [title, setTitle] = useState(initialOrder?.title || initialOrder?.code || '')
  const [lines, setLines] = useState<GridLineItem[]>(() => {
    if (!initialOrder) return []
    return initialOrder.lines.map((l) => ({
      id: l.id,
      tempKey: l.id,
      customer_id: l.customer_id || '',
      customer_code: l.customer_code || '',
      customer_name: l.customer_name || '',
      buyer_id: l.buyer_id || null,
      buyer_name: l.buyer_name || null,
      variant_id: l.variant_id,
      variant_code: l.variant_code || '',
      product_name: l.product_name || '',
      assembly_type: (l.assembly_type || '') as any,
      ista_level: (l.ista_level || '') as any,
      quantity: Number(l.quantity) || 0,
      customer_po: l.customer_po || '',
      line_status: l.line_status || 'draft',
    }))
  })
  const [customers, setCustomers] = useState<Customer[]>([])
  const [customerBuyersMap, setCustomerBuyersMap] = useState<Record<string, CustomerBuyer[]>>({})
  const [variantsPreload, setVariantsPreload] = useState<VariantSearchResult[]>([])

  const [loading, setLoading] = useState(!initialOrder)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isDirty, setIsDirty] = useState(false)

  // Modals & highlight states
  const [pasteModalOpen, setPasteModalOpen] = useState(false)
  const [errorHighlightFields, setErrorHighlightFields] = useState<{
    lineId: string
    fields: string[]
  } | null>(null)

  // Map server lines to GridLineItem
  const mapServerLinesToGrid = useCallback((serverOrder: SalesOrderDetail, variantList: VariantSearchResult[]): GridLineItem[] => {
    return serverOrder.lines.map((l) => {
      const vMatch = variantList.find((v) => v.id === l.variant_id)
      return {
        id: l.id,
        tempKey: l.id,
        customer_id: l.customer_id || '',
        customer_code: l.customer_code || '',
        customer_name: l.customer_name || '',
        buyer_id: l.buyer_id || null,
        buyer_name: l.buyer_name || null,
        variant_id: l.variant_id,
        variant_code: l.variant_code || '',
        product_name: l.product_name || vMatch?.product_name || '',
        product_id: vMatch?.product_id || '',
        has_image: Boolean(vMatch?.has_image || vMatch?.image_url),
        color_name: vMatch?.color?.name || vMatch?.color_name || '',
        color_swatch_hex: vMatch?.color?.swatch_hex || null,
        w: vMatch?.dimensions?.w ?? vMatch?.w ?? null,
        d: vMatch?.dimensions?.d ?? vMatch?.d ?? null,
        h: vMatch?.dimensions?.h ?? vMatch?.h ?? null,
        dim_unit: 'inch',
        assembly_type: (l.assembly_type || '') as any,
        ista_level: (l.ista_level || '') as any,
        quantity: Number(l.quantity) || 0,
        customer_po: l.customer_po || '',
        pcs_per_carton: l.pcs_per_carton != null ? l.pcs_per_carton : null,
        packing_note: l.packing_note || '',
        requested_ship_date: l.requested_ship_date || '',
        updated_at: l.updated_at || '',
        line_status: l.line_status || 'draft',
        status_reason: l.status_reason || null,
        demand_status: l.demand_status || 'NOT_PUSHED',
        demand_batch_ids: l.demand_batch_ids || [],
        note: l.note || '',
      }
    })
  }, [])

  // Initial Load
  const loadAll = useCallback(async () => {
    if (!id) return
    setLoading(true)
    setError(null)

    try {
      const [orderRes, custList, varList] = await Promise.all([
        getSalesOrderApi(id),
        listCustomersApi({ status: 'all' }),
        searchVariantsApi(''),
      ])

      if (!orderRes) {
        setOrder(null)
        return
      }

      setOrder(orderRes)
      setTitle(orderRes.title || orderRes.code)
      setCustomers(custList)
      setVariantsPreload(varList)

      const gridLines = mapServerLinesToGrid(orderRes, varList)
      setLines(gridLines)
      setIsDirty(false)

      // Load buyers for customers in order
      const uniqueCustIds = Array.from(
        new Set(
          [
            orderRes.customer_id,
            ...orderRes.lines.map((l) => l.customer_id),
          ].filter(Boolean) as string[],
        ),
      )

      const buyersMap: Record<string, CustomerBuyer[]> = {}
      await Promise.all(
        uniqueCustIds.map(async (cId) => {
          try {
            const detail = await getCustomerApi(cId)
            if (detail?.buyers) {
              buyersMap[cId] = detail.buyers
            }
          } catch {
            // Ignore single customer buyer fetch error
          }
        }),
      )
      setCustomerBuyersMap(buyersMap)
    } catch (err: any) {
      setError(err instanceof Error ? err.message : 'Không thể tải dữ liệu đơn hàng')
    } finally {
      setLoading(false)
    }
  }, [id, mapServerLinesToGrid])

  useEffect(() => {
    void loadAll()
  }, [loadAll])

  // Prevent leaving with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault()
        e.returnValue = ''
      }
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [isDirty])

  // Handle lines update from grid
  const handleUpdateLines = (newLines: GridLineItem[]) => {
    setLines(newLines)
    setIsDirty(true)
  }

  // Handle save
  const handleSave = async () => {
    if (!id || !order) return
    if (!canWrite) return

    // 1. Validation check
    let firstInvalidLineId: string | null = null
    let invalidCount = 0

    for (const l of lines) {
      const v = validateGridLine(l)
      if (!v.valid) {
        invalidCount++
        if (!firstInvalidLineId) {
          firstInvalidLineId = l.id || l.tempKey
          setErrorHighlightFields({
            lineId: l.id || l.tempKey,
            fields: v.missingFields,
          })
        }
      }
    }

    if (invalidCount > 0) {
      alert(`⚠️ Còn ${invalidCount} dòng chưa điền đủ thông tin bắt buộc (Khách hàng, Mã hàng, Ráp/Tháo, ISTA, Số lượng). Vui lòng kiểm tra lại.`)
      return
    }

    setSaving(true)
    setErrorHighlightFields(null)

    try {
      const patchLinesPayload = buildPatchLinesPayload(lines)
      const updated = await patchSalesOrderApi(id, {
        title: title.trim() || null,
        lines: patchLinesPayload,
      })

      setOrder(updated)
      setTitle(updated.title || updated.code)
      setLines(mapServerLinesToGrid(updated, variantsPreload))
      setIsDirty(false)
      alert('✓ Đã lưu đơn hàng thành công!')
    } catch (err: any) {
      const formatted = formatDocumentError(err)
      alert(`❌ Lỗi khi lưu đơn hàng: ${formatted.message}`)
    } finally {
      setSaving(false)
    }
  }

  // Handle Line Status change via dedicated API
  const handleLineStatusChange = async (
    lineId: string,
    targetStatus: 'draft' | 'sx' | 'cancelled',
    reason?: string,
  ) => {
    if (!id) return
    setErrorHighlightFields(null)

    try {
      const updated = await updateSalesOrderLineStatusApi(id, lineId, {
        status: targetStatus,
        reason,
      })

      setOrder(updated)
      setLines(mapServerLinesToGrid(updated, variantsPreload))
      setIsDirty(false)
    } catch (err: any) {
      if (err?.code === 'SX_REQUIREMENTS_MISSING' && err?.details?.fields) {
        setErrorHighlightFields({
          lineId,
          fields: err.details.fields,
        })
        const fieldLabels: Record<string, string> = {
          customer_id: 'Khách hàng',
          variant_code: 'Mã hàng',
          assembly_type: 'Ráp/Tháo',
          ista_level: 'ISTA',
          quantity: 'Số lượng',
        }
        const missingLabels = (err.details.fields as string[]).map((f) => fieldLabels[f] || f)
        alert(`❌ Không thể chuyển sang SX! Thiếu các trường bắt buộc:\n• ${missingLabels.join('\n• ')}`)
        return
      }

      const formatted = formatDocumentError(err)
      alert(`❌ ${formatted.message}`)
      throw err
    }
  }

  // Paste from Excel import handler
  const handleImportPastedLines = (pastedLines: any[]) => {
    const newItems: GridLineItem[] = pastedLines.map((p) => {
      const cust = customers.find((c) => c.code.toUpperCase() === p.customer_code.toUpperCase())
      const v = variantsPreload.find((vm) => vm.variant_code.toUpperCase() === p.variant_code.toUpperCase())

      return {
        tempKey: `line-paste-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        customer_id: cust?.id || '',
        customer_code: cust?.code || p.customer_code,
        customer_name: cust?.name || '',
        buyer_id: null,
        buyer_name: null,
        variant_id: v?.id || '',
        variant_code: v?.variant_code || p.variant_code,
        product_name: v?.product_name || '',
        product_id: v?.product_id || '',
        has_image: Boolean(v?.has_image || v?.image_url),
        color_name: v?.color?.name || v?.color_name || '',
        color_swatch_hex: v?.color?.swatch_hex || null,
        w: v?.dimensions?.w ?? v?.w ?? null,
        d: v?.dimensions?.d ?? v?.d ?? null,
        h: v?.dimensions?.h ?? v?.h ?? null,
        dim_unit: 'inch',
        assembly_type: p.assembly_type || '',
        ista_level: p.ista_level || '',
        quantity: p.quantity || 1,
        customer_po: p.customer_po || '',
        pcs_per_carton: null,
        packing_note: '',
        requested_ship_date: '',
        updated_at: '',
        line_status: 'draft',
        status_reason: null,
      }
    })

    setLines((prev) => [...prev, ...newItems])
    setIsDirty(true)
  }

  if (loading) return <PageLoading label="Đang tải dữ liệu đơn hàng..." />
  if (error) return <PageError message={error} onRetry={loadAll} />
  if (!order) {
    return (
      <PageNotFound
        title="Không tìm thấy đơn hàng"
        description={`Không tìm thấy đơn hàng: ${id}`}
        action={
          <Link to="/sales/orders" className="text-emerald-700 underline font-medium">
            Quay lại danh sách
          </Link>
        }
      />
    )
  }

  return (
    <div className="space-y-3">
      {/* Breadcrumb: Sale > Đơn hàng > {title || code} */}
      <Breadcrumb
        items={[
          { label: 'Trang chủ', to: '/' },
          { label: 'Sale', to: '/sales' },
          { label: 'Đơn hàng', to: '/sales/orders' },
          { label: title || order.code, to: `/sales/orders/${order.id}` },
        ]}
      />

      {/* Top Title & Header Card: NO customer/buyer/date block, NO 'Nháp' badge at table level */}
      <div className="flex flex-wrap items-center justify-between gap-4 rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex flex-wrap items-center gap-3">
          {canWrite ? (
            <input
              type="text"
              value={title}
              onChange={(e) => {
                setTitle(e.target.value)
                setIsDirty(true)
              }}
              placeholder="Nhập tên bảng đơn hàng..."
              className="text-xl font-extrabold text-slate-900 border-b border-dashed border-slate-400 bg-transparent outline-none focus:border-emerald-600 focus:bg-emerald-50/40 px-1 py-0.5 min-w-[280px]"
              title="Bấm vào để đổi tên bảng đơn hàng"
            />
          ) : (
            <h2 className="text-xl font-extrabold text-slate-900 px-1">{title}</h2>
          )}

          <span
            className="rounded border border-slate-300 bg-slate-100 px-2 py-0.5 font-mono text-xs font-bold text-slate-600"
            title="Mã đơn hàng hệ thống tự sinh"
          >
            {order.code}
          </span>

          {isDirty && (
            <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-800 animate-pulse">
              ● Có thay đổi chưa lưu
            </span>
          )}
        </div>

        {/* Action buttons: Dán từ Excel, Lưu */}
        <div className="flex items-center gap-2">
          {canWrite && (
            <>
              <Button
                variant="secondary"
                size="sm"
                className="text-xs"
                onClick={() => setPasteModalOpen(true)}
              >
                📋 Dán từ Excel
              </Button>

              <Button
                variant="primary"
                size="sm"
                className="text-xs font-bold"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Đang lưu...' : <><Save size={14} aria-hidden="true" /> Lưu</>}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Excel-style Grid */}
      <SalesOrderGrid
        lines={lines}
        customers={customers}
        customerBuyersMap={customerBuyersMap}
        variantsPreload={variantsPreload}
        canWrite={canWrite}
        canDemandWrite={canDemandWrite}
        orderId={order.id}
        orderCode={order.code}
        onUpdateLines={handleUpdateLines}
        onLineStatusChange={handleLineStatusChange}
        onSave={handleSave}
        saving={saving}
        errorHighlightFields={errorHighlightFields}
      />

      {/* Paste from Excel Modal */}
      <PasteExcelModal
        isOpen={pasteModalOpen}
        onClose={() => setPasteModalOpen(false)}
        onImport={handleImportPastedLines}
      />
    </div>
  )
}
